// functions
import { connect } from 'react-redux';
import * as ComponentFunctions from './ProtocolContract.Functions';
import { Spinner } from '@fluentui/react';
import { Link, useNavigate } from 'react-router-dom';
import ContractsSelectorComponent from '../../../../commons/component/Inputs/ContractSelector/ContractSelectorComponent';
import { useEffect, useState } from 'react';
import moment from 'moment';
import { Protocol } from '../../../../models/Entities/Protocols/Protocol';
import { Contract } from '../../../../models/Entities/Contracts/Contract';
import { Badge, Button, Text, Divider } from '@fluentui/react-components';
import { LinkRegular, CalendarLtrRegular, DocumentTextRegular, PlugConnectedRegular } from '@fluentui/react-icons';

////i18Next
import { useTranslation, Trans } from 'react-i18next';

// Styles
import './ProtocolContract.Styles.css';
import { ProtocolContract } from '../../../../models/Entities/Protocols/ProtocolContracts';
import { ConfirmationButtonComponent } from '../../../../commons/component/ConfirmationButton/ConfirmationButtonComponent';


const getStatusBadgeColor = (status: number | undefined): 'success' | 'warning' | 'danger' | 'informative' => {
    switch (status) {
        case 1: return 'success';
        case 2: return 'warning';
        case 3: return 'warning';
        case 4: return 'danger';
        case 5: return 'danger';
        case 6: return 'informative';
        default: return 'informative';
    }
};


export const ProtocolContractComponent = (props: ComponentFunctions.Props) => {

    const navigate = useNavigate();
    const [selectedContractToAssociate, setSelectedContractToAssociate] = useState<Contract | undefined>(undefined);
    const [verificationStatus, setVerificationStatus] = useState<boolean | undefined>(undefined);
    const [ContractProtocolAssociated, setContractProtocolAssociated] = useState<Protocol | undefined>(undefined);
    const [ContractAssociated, setContractAssociated] = useState<Contract | undefined>(undefined);

    const [t] = useTranslation();

    // effects
    useEffect(() => {
        setVerificationStatus(selectedContractToAssociate?.id === null ? true : false); 
        if(selectedContractToAssociate?.id !== null) {
            setContractProtocolAssociated(props.Protocols.find((protocol: Protocol) => protocol.id === selectedContractToAssociate?.id));
        }
        
    }, [selectedContractToAssociate !== undefined]);

    useEffect(() => {
        props.Protocol && props.GetAllProtocolContractAssociations(props.ProtocolId);
    }, [props.ProtocolId, props.Protocol !== undefined]);

    useEffect(() => {
        props.Protocol?.contractAssociation?.contractId && props.GetContractById(props.Protocol?.contractAssociation.contractId);
    }, [props.Protocol?.contractAssociation?.contractId]);

    //events
    const onViewcontractDetailsEvent = (contractId: number) => {
        navigate(`/contracts/${contractId}`);
    };

    const setProtocolContract = (contractId: number) => {
        props.AssociateContractToProtocol(contractId, props.Protocol?.id);
    };

    const onDisassociateContract = (contractAssociation: ProtocolContract) => {
        props.DisassociateContractFromProtocol(contractAssociation);
    };

    const ContractInfoCard = ({ contract, isPreview }: { contract: Contract | undefined; isPreview?: boolean }) => (
        <div className={`pc-card${isPreview ? ' pc-card--preview' : ''}`}>
            <div className="pc-card__header">
                <DocumentTextRegular className="pc-card__doc-icon" />
                <div className="pc-card__title-group">
                    <Link to={`/contracts/${contract?.id}`} target="_tab" rel="noopener noreferrer" className="pc-card__contract-link">
                        {contract?.contractNumber}
                    </Link>
                    {contract?.contractDescription && (
                        <Text size={200} className="pc-card__description">{contract.contractDescription}</Text>
                    )}
                </div>
                <Badge
                    appearance="tint"
                    color={getStatusBadgeColor(contract?.contractStatus)}
                    className="pc-card__status-badge"
                >
                    {ComponentFunctions.GetContractStatusDescripcion(contract?.contractStatus)}
                </Badge>
            </div>
            <Divider />
            <div className="pc-card__dates">
                <CalendarLtrRegular className="pc-card__date-icon" />
                <span className="pc-card__date-label">{t('protocolContract.contract.validity')}</span>
                <strong>
                    {contract?.contractStartDate
                        ? moment(contract.contractStartDate).format('DD/MM/YYYY')
                        : t('protocolContract.contractStartDate.notDefined')}
                </strong>
                <span className="pc-card__date-sep">→</span>
                <strong>
                    {contract?.contractEndDate
                        ? moment(contract.contractEndDate).format('DD/MM/YYYY')
                        : t('protocolContract.contractEndDate.notDefined')}
                </strong>
            </div>
        </div>
    );

    const noContract = (
        <div className="pc-section">
            <div className="pc-empty-state">
                <LinkRegular className="pc-empty-state__icon" />
                <Text size={400} weight="semibold">{t('protocolContract.noContract.unassociatedProtocol')}</Text>
                <Text size={200} className="pc-empty-state__hint">{t('protocolContract.noContract.contract')}</Text>
            </div>
            <Divider />
            <div className="pc-selector">
                <ContractsSelectorComponent
                    onSelectedContract={(selectedContracts: Contract[]) => setSelectedContractToAssociate(selectedContracts[0])}
                    ContractsSelected={props.Protocol?.contractAssociation?.contractId ? [props.Protocol?.contractAssociation.protocolId] : []}
                    MultiselectEnable={false}
                />
            </div>
            {selectedContractToAssociate && (
                <>
                    <ContractInfoCard contract={selectedContractToAssociate} isPreview />
                    <div className="pc-actions">
                        <Button
                            appearance="primary"
                            icon={<PlugConnectedRegular />}
                            disabled={!verificationStatus}
                            onClick={() => setProtocolContract(selectedContractToAssociate.id)}
                        >
                            <Trans i18nKey={'protocolContract.buttons.associateContract'} />
                        </Button>
                    </div>
                </>
            )}
        </div>
    );

    const ContractView = (
        <div className="pc-section">
            <div className="pc-section__toolbar">
                <ConfirmationButtonComponent
                    ButtonId={'disassociateContract'}
                    ButtonIconName={'RemoveLink'}
                    ButtonText={''}
                    ButtonCssClass={'danger'}
                    ConfirmationButtonText={t('protocolContract.buttons.disassociate')}
                    ConfirmationButtonCssClass={'danger'}
                    CancelButtonText={t('protocolContract.buttons.cancelButton')}
                    CancelButtonCssClass={''}
                    Title={t('protocolContract.buttons.disassociateContract')}
                    Description={`Va a desasociar el contrato ${props.Contract?.contractNumber} del protocolo ${props.Protocol?.fullTitle}`}
                    ElementTextHeader={props.Contract?.contractNumber || ''}
                    ElementTextInformation={t('protocolContract.buttons.information')}
                    isOperationExecuting={false}
                    isOperationExecutedWithError={false}
                    OperationError={undefined}
                    onConfirmation={() => props.Protocol?.contractAssociation && onDisassociateContract(props.Protocol?.contractAssociation)}
                    onCancel={() => {}}
                />
            </div>
            <ContractInfoCard contract={props.Contract} />
        </div>
    );

    return (
        <div className="pc-container Section Internal">
            {props.Protocol
                ? props.Protocol?.contractAssociation?.contractId
                    ? ContractView
                    : noContract
                : <div className="pc-loading"><Spinner label={t('protocolContract.onVerification.loading')} ariaLive="assertive" labelPosition="right" /></div>
            }
        </div>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(ProtocolContractComponent as any);